import { useCallback, useEffect, useState } from "react";
import {
  listManufacturingRecipes,
  type MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

/**
 * Lightweight org-recipe list (no detail / lines). Used by the Receive
 * panel's "Recipe count" form to populate the recipe picker. Mirrors the
 * other entitlement-gated hooks: `enabled` short-circuits the fetch and
 * `refresh` re-pulls in a working state.
 */
export function useManufacturingRecipesList(opts: { enabled: boolean }) {
  const { enabled } = opts;
  const scopedOrganizationId = useScopedOrganizationId();
  const [recipes, setRecipes] = useState<MfgRecipeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (mode?: { refresh?: boolean }) => {
      if (!enabled) {
        setRecipes([]);
        setLoading(false);
        setWorking(false);
        return;
      }
      if (mode?.refresh) setWorking(true);
      else setLoading(true);
      setError(null);
      const r = await listManufacturingRecipes();
      if (!r.ok) {
        setRecipes([]);
        setError(r.error);
        setLoading(false);
        setWorking(false);
        return;
      }
      setRecipes(Array.isArray(r.data.recipes) ? r.data.recipes : []);
      setLoading(false);
      setWorking(false);
    },
    [enabled]
  );

  useEffect(() => {
    void load();
  }, [load, scopedOrganizationId]);

  return {
    recipes,
    loading,
    working,
    error,
    refresh: () => void load({ refresh: true }),
  };
}
